import { ReactNode } from 'react';
import {
  Col,
  ControlLabel,
  Glyphicon,
  HelpBlock,
  InputGroup,
} from 'react-bootstrap';
import { Controller, get, useFormContext } from 'react-hook-form';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import ErrorMessage from '../_errorMessage/ErrorMessage';
import Tooltip from '../../tooltip/Tooltip';
import {
  FormGroupContainer,
  InputGroupTooltipWrapper,
  LabelTooltip,
} from './GenericFormField.styles';
import { GenericFormFieldProps } from './GenericFormField.types';

function getInfoTooltipElement({
  name,
  tooltipText,
  id,
  placement,
}: {
  name: string;
  tooltipText: string | ReactNode;
  id?: string;
  placement?: 'top' | 'right' | 'bottom' | 'left';
}) {
  const iconId = `${name}-tooltip-icon${id ? `-${id}` : ''}`;

  return (
    <Tooltip
      text={tooltipText}
      id={`${name}-tooltip${id ? `-${id}` : ''}`}
      placement={placement}
    >
      <Glyphicon glyph="info-sign" id={iconId} data-testid={iconId} />
    </Tooltip>
  );
}

const GenericFormField: React.FC<GenericFormFieldProps> = ({
  name,
  groupName,
  fieldRenderer,
  label,
  help,
  leftAddon,
  rightAddons,
  rightTooltip,
  rightTooltipPlacement,
  colSmInput,
  colSmLabel,
  colSmOffsetInput,
  colXsInput,
  colXsLabel,
  colXsOffsetInput,
  containerClassName,
  labelOnTop,
  required,
  inline,
  secondaryField,
  disabled,
  isValid: isValidProp,
  isLoading,
  labelTooltip,
}) => {
  const {
    formState: { errors, dirtyFields },
    control,
  } = useFormContext();

  const hasErrors = !!get(errors, name);
  const isValid =
    isValidProp !== undefined
      ? isValidProp
      : !!get(dirtyFields, name) && !get(errors, name);

  const validationState = () => {
    if (hasErrors) {
      return 'error';
    }
    if (isValid) {
      return 'success';
    }
    return undefined;
  };

  const renderInput = () => {
    const renderInputGroup = () => {
      const inputGroupBase = (
        <InputGroup>
          {leftAddon && <InputGroup.Addon>{leftAddon}</InputGroup.Addon>}
          <Controller control={control} name={name} render={fieldRenderer} />
          {!!rightAddons?.length &&
            rightAddons.map((rightAddon, index) => (
              // Using index as a key here should be fine considering the array
              // order and size will not change between renders.
              <InputGroup.Addon key={index}>{rightAddon}</InputGroup.Addon>
            ))}
        </InputGroup>
      );
      return rightTooltip ? (
        <InputGroupTooltipWrapper>
          {inputGroupBase}
          {getInfoTooltipElement({
            name,
            tooltipText: rightTooltip,
            placement: rightTooltipPlacement,
          })}
        </InputGroupTooltipWrapper>
      ) : (
        inputGroupBase
      );
    };

    return (
      <>
        {/*
         // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified */}
        {leftAddon || rightAddons?.length > 0 || rightTooltip ? (
          renderInputGroup()
        ) : (
          <Controller control={control} name={name} render={fieldRenderer} />
        )}
        {help && <HelpBlock>{help}</HelpBlock>}
        {errors && get(errors, name) && (
          /* @ts-ignore */
          <ErrorMessage error={get(errors, name).message} />
        )}
      </>
    );
  };

  if (inline) {
    return (
      <FormGroupContainer
        $disabled={disabled}
        $customClassName={containerClassName}
        $name={groupName || name}
        playsInline
        validationState={validationState()}
      >
        {renderInput()}
      </FormGroupContainer>
    );
  }

  const fieldSizeDivider = secondaryField ? 2 : 1;
  const fieldXsSize =
    (colXsInput || (labelOnTop || !label ? 12 : 8)) / fieldSizeDivider;
  const fieldSmSize =
    (colSmInput || (labelOnTop || !label ? 12 : 10)) / fieldSizeDivider;

  return (
    <FormGroupContainer
      $disabled={disabled}
      $customClassName={containerClassName}
      $name={groupName || name}
      validationState={validationState()}
    >
      {label &&
        (labelOnTop ? (
          <Col
            componentClass={ControlLabel}
            className="label-on-top"
            data-testid={`${groupName || name}-field-label`}
            required={required}
            xs={12}
          >
            {label}
            {labelTooltip && (
              <LabelTooltip $marginLeft={required ? 16 : 8}>
                {getInfoTooltipElement({
                  name,
                  tooltipText: labelTooltip,
                  id: 'label-tooltip',
                })}
              </LabelTooltip>
            )}
          </Col>
        ) : (
          <Col
            componentClass={ControlLabel}
            data-testid={`${groupName || name}-field-label`}
            xs={colXsLabel || 4}
            sm={colSmLabel || 2}
            required={required}
          >
            {label}
            {labelTooltip && (
              <LabelTooltip $marginLeft={required ? 16 : 8}>
                {getInfoTooltipElement({
                  name,
                  tooltipText: labelTooltip,
                  id: 'label-tooltip',
                })}
              </LabelTooltip>
            )}
          </Col>
        ))}

      <Col
        xs={fieldXsSize}
        xsOffset={colXsOffsetInput}
        sm={fieldSmSize}
        smOffset={colSmOffsetInput}
      >
        {isLoading ? (
          <LoadingPlaceholder width="100%" linesCount={1.5} margin="8px 0 0" />
        ) : (
          renderInput()
        )}
      </Col>
      {!!secondaryField && (
        <Col
          xs={fieldXsSize}
          xsOffset={colXsOffsetInput}
          sm={fieldSmSize}
          smOffset={colSmOffsetInput}
        >
          {secondaryField}
        </Col>
      )}
    </FormGroupContainer>
  );
};

GenericFormField.defaultProps = {
  label: '',
  help: '',
  leftAddon: '',
  rightAddons: [],
  rightTooltip: '',
  colSmInput: 10,
  colSmOffsetInput: 0,
  colSmLabel: 2,
  labelOnTop: false,
  disabled: false,
};

export default GenericFormField;
